const feature = {
  slug: "instagram-dm-automation",
  path: "/features/instagram-dm-automation",
  eyebrow: "Instagram DM automation",
  h1: "Instagram DM automation that takes the order, not just the message",
  seoTitle: "Instagram DM Automation for Sellers | Checkout DM",
  description:
    "Checkout DM replies to Instagram DMs 24/7, shows your catalog, collects order details and takes payment in the chat. See the full flow and who it suits.",
  intro:
    "Most Instagram DM automation stops after the first reply. Checkout DM keeps going: it shows your products, asks for the size, collects the delivery address, sends the payment link and records the order, all inside the same Instagram conversation.",
  glance: [
    { label: "Available on", value: "Every plan, including Free" },
    { label: "Free plan limits", value: "5 products, 15 conversations a month" },
    { label: "Connects through", value: "Meta’s official Instagram login" },
  ],
  blocks: [
    { type: "h2", text: "What it does while you are busy" },
    {
      type: "p",
      text:
        "A customer messages your Instagram account at any hour. Without you typing anything, Checkout DM does the repeatable parts of the sale:",
    },
    {
      type: "ul",
      items: [
        "**Replies instantly** and shows your catalog, with the photo, name, price and sizes of each product exactly as you entered them.",
        "**Checks stock.** If you track quantity, a product or size that sells out is not offered again.",
        "**Collects the order details** one step at a time: size, quantity, name, phone number and delivery address.",
        "**Sends payment.** With Razorpay, Stripe or PayPal connected, the customer pays through a secure link in the chat. With manual payment, they pay by UPI, QR code or bank transfer.",
        "**Records the order** in your dashboard with an order number, the items and the amount, and sends order emails to the customer.",
        "**Answers the standard questions.** Your return policy, exchange policy and FAQs are shown automatically in DMs on every plan.",
      ],
    },

    { type: "h2", text: "See the conversation" },
    {
      type: "chat",
      shop: "yourshop",
      title: "At 11:40 PM",
      caption: "Illustrative example. Your own messages use your products and wording.",
      messages: [
        { from: "time", text: "Customer messages after you have closed for the day" },
        { from: "buyer", text: "Hi, do you have the linen kurta set?" },
        { from: "shop", text: "Yes! Linen Kurta Set · ₹1,899. Tap to select it." },
        { from: "buyer", text: "Select this" },
        { from: "shop", text: "Which size and how many would you like?" },
        { from: "buyer", text: "M, 1" },
        { from: "shop", text: "Added to your cart. Would you like to add anything else before checkout?" },
        { from: "buyer", text: "No, checkout" },
        { from: "shop", text: "Great. What name should we put on the order?" },
        { from: "time", text: "Address, phone and payment follow, one step at a time" },
      ],
    },
    {
      type: "p",
      text:
        "The buyer never leaves Instagram, and you wake up to an order instead of a message you now have to chase. For a deeper walkthrough, read [how to automate sales on Instagram](/blog/how-to-automate-sales-on-instagram).",
    },

    { type: "h2", text: "How hands-off the payment step is" },
    {
      type: "p",
      text:
        "This depends on how you collect money. With a payment gateway, payments are confirmed automatically and the order is marked paid without you. With manual UPI, QR or bank transfer, the customer sends a screenshot and you press Confirm or Reject after checking your bank account. Manual payment works from the Free plan. Our guide to [UPI versus a payment gateway](/blog/accept-payments-instagram-upi-vs-gateway) explains which to choose.",
    },

    { type: "h2", text: "When a human is needed" },
    {
      type: "p",
      text:
        "Checkout DM runs a structured checkout flow rather than open-ended chat. When it cannot understand a reply, or a customer asks for a person, the conversation is handed to you. On Starter, Growth and Pro, the Stuck tab lists the chats that need you, such as a customer who asked for a human, an untouched cart or a conversation with no reply for 7 days or more. Pause Bot lets you take over a chat without the bot talking over you.",
    },

    { type: "h2", text: "Built to stay inside Meta’s rules" },
    {
      type: "p",
      text:
        "You connect through Instagram’s own login screen, so your password is never shared and you can remove access from your Instagram settings at any time. Messages follow Meta’s published limits, including one private reply per comment within 7 days and conversation replies within 24 hours of the customer’s last message. Read our guides on [whether Instagram DM automation is safe](/blog/is-instagram-dm-automation-safe-2026) and [how Checkout DM connects](/blog/is-checkout-dm-safe-instagram-oauth).",
    },

    { type: "h2", text: "What it does not do" },
    {
      type: "ul",
      items: [
        "**It is not a website builder.** From the Creator plan you get a shareable Storefront link, but your store lives at a checkoutdm.com address.",
        "**Chat automation runs on Instagram.** Comments, Story replies and DMs are automated, and there is no WhatsApp, TikTok or SMS chat automation. From the Creator plan, your Storefront link can still be shared anywhere, and buyers do not need Instagram to use it. If you need chat automation on other channels, see our [comparison with other tools](/compare).",
        "**It is built for selling.** If you only want to answer messages and never take an order in the chat, a simpler tool may be enough.",
      ],
    },

    { type: "h2", text: "Who it suits" },
    {
      type: "p",
      text:
        "Checkout DM suits Instagram sellers whose customers already start in DMs: clothing, jewellery, handmade goods, beauty products and digital creators selling files or courses. It is made for sellers who currently take orders by hand, from replies to payment screenshots to addresses typed across five messages.",
    },

    { type: "h2", text: "Start on the Free plan" },
    {
      type: "ol",
      items: [
        "Connect your Instagram professional account through Instagram’s login.",
        "Add up to 5 products with photos, prices and sizes.",
        "Choose how you get paid: UPI, QR code and bank transfer, or a payment gateway.",
        "Place a test order from a second Instagram account and follow it as a customer.",
        "Tell your followers to message you, then watch the first real orders arrive.",
      ],
    },
  ],
  related: [
    { to: "/features/instagram-comment-to-dm-automation", title: "Comment-to-DM automation", description: "Turn comments on your Reels and posts into private conversations." },
    { to: "/features/instagram-story-reply-automation", title: "Story reply automation", description: "Turn one word on your Story into a product card." },
    { to: "/features/instagram-product-catalog-checkout", title: "Catalog and checkout", description: "Products, sizes, stock, payment and delivery details in the chat." },
    { to: "/pricing", title: "Checkout DM pricing", description: "From Free to Pro, with no commission on any plan." },
  ],
  faqs: [
    {
      q: "Is Instagram DM automation safe for my account?",
      a: "Checkout DM connects through Meta’s official Instagram login, so your password is never shared, and you can remove its access from your Instagram settings at any time. It also works within Meta’s published messaging limits.",
    },
    {
      q: "Do I need a website to use Checkout DM?",
      a: "No. Customers browse, choose and pay inside the Instagram chat. From the Creator plan you also get a Storefront link you can share anywhere.",
    },
    {
      q: "What happens if the bot cannot answer?",
      a: "The conversation is handed to you. On Starter, Growth and Pro, the Stuck tab lists the chats that need a person, and Pause Bot lets you take over a chat.",
    },
    {
      q: "Can Checkout DM automate WhatsApp or TikTok?",
      a: "No. Chat automation works on Instagram only today. But from the Creator plan, you can share your Storefront link on WhatsApp, Telegram, YouTube, Facebook or any website, and customers can buy from it without Instagram. If you need chat automation on other channels, another tool is a better fit.",
    },
    {
      q: "What does it cost?",
      a: "The Free plan is ₹0 with 5 products and 15 unique customer conversations a month. Paid plans start at ₹499 a month. There is no commission or transaction fee on any plan.",
    },
  ],
};

export default feature;
